<style lang="less" scoped>
  .dashboard-container {
    width: 100%;
    padding: 40px;
    overflow: hidden;
    > * {
      flex-shrink: 0;
    }
  }
  .left-section {
    float: left;
    width: calc((100% - 40px) * 4 / 10);
    min-width: 350px;
  }
  .title {
    font-size: 18px;
  }
  .section-box {
    display: flex;
    flex-direction: column;
    margin-top: 20px;
    border: 1px solid #e3e3e3;
    padding-bottom: 40px;
  }
  .right-section {
    float: left;
    width: calc((100% - 40px) * 6 / 10);
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    margin-left: 40px;

    &.is-single {
      width: 100%;
      margin-left: 0;
      padding-right: 0;
    }
  }
  .right-section-header {
    display: flex;
    align-items: center;
  }
  .tools {
    margin-left: auto;
  }
  .chart {
    width: 100%;
    height: 337px;
  }
  .chart-legend-group {
    display: flex;
    align-items: center;
    margin-top: 20px;
  }
  .chart-legend {
    display: flex;
    align-items: center;

    &:not(:first-of-type) {
      margin-left: 20px;
    }
  }
  .chart-legend-indicator {
    width: 16px;
    height: 2px;

    &.skyblue { background-color: #73dff1; }
    &.pink { background-color: #ff92a7; }
    &.purple { background-color: #d093ff; }
  }
  .chart-legend-label {
    margin-left: 6px;
    color: #808080;
    font-size: 13px;
  }
  .advert-results-table {
    margin-top: 40px;
  }
</style>

<template lang="pug">
  section.dashboard-container
    .left-section(v-if="me.type === 'cpc'")
      h1.title 광고주 현황
      .section-box(style="border-bottom: 0;")
        sc-tabs(:currentTab="currentTab", :tabs="tabs", @change="changeTab")
      el-table.advertiser-table.table-type-1(
        :data="advertisers",
        :border="true")
        el-table-column(prop="rank", label="순위")
        el-table-column(prop="name", label="상호명")
        el-table-column(label="입찰가")
          template(slot-scope="scope") {{ scope.row.bidPrice | displayPrice }}
        el-table-column(label="누적 충전금")
          template(slot-scope="scope") {{ scope.row.accumulatedMoney | displayPrice }}
    .right-section(:class="{ 'is-single': me.type === 'cpc' ? false : true }")
      .right-section-header
        h1.title 성과요약
        .tools
          filter-date-range(
            :hasDevice="false",
            :hasDateFilter="true",
            :dateFilter="query.dateFilter",
            :dateRange="dateRange",
            :downloadName="downloadName",
            :downloadUrl="downloadUrl",
            :download="download",
            @changeDateFilter="changeQuery($event, 'dateFilter')",
            @changeDevice="changeQuery($event, 'device')",
            @changeDate="changeQuery($event, 'date')"
            @excelDownload="excelDownload",
            @clearDownload="clearDownload",
          )
      .section-box(style="padding-bottom: 0;")
        chartist.chart(
          type="Line",
          :data="{labels: searchDates, series: chartistDate}",
          :options="chartistOptions",
        )
      .chart-legend-group
        .chart-legend
          .chart-legend-indicator.skyblue
          p.chart-legend-label 주문건수
        .chart-legend
          .chart-legend-indicator.pink
          p.chart-legend-label 클릭수
        .chart-legend
          .chart-legend-indicator.purple
          p.chart-legend-label 회원가입
      div(v-loading="loading")
        el-table.advert-results-table.table-type-1(
          v-if="!loading",
          :data="advertResults",
          :border="true",
          :show-summary="true",
          :summary-method="getSummaries")
          el-table-column(label="날짜", width="120", align="center", header-align="center")
            template(slot-scope="scope") {{ scope.row.statsDay | displayDate }}
          el-table-column(prop="validCount", label="클릭수", align="center", header-align="center")
          el-table-column(prop="buyCount", label="주문건수", align="center", header-align="center")
          el-table-column(prop="buyAmount", label="매출", align="center", header-align="center")
            template(slot-scope="scope") {{ scope.row.buyAmount | displayPrice }}
          el-table-column(prop="cvr", label="구매전환율", align="center", header-align="center")
            template(slot-scope="scope") {{ scope.row.buyCount | displayRatio(scope.row.validCount) }}
          el-table-column(prop="joinCount", label="회원가입수", align="center", header-align="center")
          el-table-column(prop="moneyAmountSum", label="광고비", align="center", header-align="center")
            template(slot-scope="scope") {{ scope.row.moneyAmountSum | displayPrice }}
          el-table-column(prop="buyAverage", label="객단가", align="center", header-align="center")
            template(slot-scope="scope") {{ scope.row.buyAmount | displayAverage(scope.row.buyCount) | displayPrice }}
          el-table-column(prop="cpcAverage", label="평균 CPC", align="center", header-align="center")
            template(slot-scope="scope") {{ scope.row.moneyAmountSum | displayAverage(scope.row.validCount) | displayPrice }}
          el-table-column(prop="roas", label="성과율", align="center", header-align="center")
            template(slot-scope="scope") {{ scope.row.buyAmount | displayRoas(scope.row.moneyAmountSum) }}
</template>

<script>
import moment from 'moment';
import _ from 'lodash';
import { mapState } from 'vuex';
import ScTabs from '@/components/ScTabs.vue';
import FilterDateRange from '@/components/FilterDateRange.vue';

export default {
  name: 'Managementdashboard',
  components: {
    ScTabs,
    FilterDateRange,
  },
  filters: {
    displayPrice(price) {
      if (!price) return '0';
      return `${price.toLocaleString()}₩`;
    },
    displayDate(date) {
      return moment(date).format('YYYY.MM.DD ddd');
    },
    displayRatio(numerator, denominator) {
      const result = Math.floor(((Number(numerator) / Number(denominator)) * 10000)) / 100;
      return Number.isNaN(result) ? '0%' : `${result}%`;
    },
    displayRoas(numerator, denominator) {
      const result = Math.floor(Number(numerator) / Number(denominator) * 10000) / 100;
      return Number.isNaN(result) ? '0%' : `${result}%`;
    },
    displayAverage(numerator, denominator) {
      const result = Math.floor(Number(numerator) / Number(denominator));
      return Number.isNaN(result) ? 0 : result;
    },
  },
  computed: {
    ...mapState({
      me: state => state.me,
      query: state => state.route.query,
    }),
  },
  watch: {
    dateRange() {
      this.initData();
    },
  },
  data() {
    return {
      currentTab: 'mobile',
      tabs: [
        { label: '모바일', value: 'mobile' },
        { label: '데스크탑', value: 'desktop' },
      ],
      advertisers: null,
      advertResults: null,
      // advertisers: DummyAdvertisersData,
      // advertResults: DummyAdvertCPSResultsData,
      chartistDate: null,
      chartistOptions: {
        lineSmooth: false,
        axisX: {
          showGrid: false,
        },
        axisY: {
          showGrid: false,
          showLabel: false,
        },
        showPoint: false,
        chartPadding: {
          top: 20,
          right: 20,
          bottom: 0,
          left: 0,
        },
      },
      searchDates: null,
      device: '',
      dateRange: [
        moment().subtract(1, 'month'),
        moment(),
      ],

      downloadUrl: null,
      downloadName: null,
      download: false,

      dateFilter: 'monthly',
      loading: false,
    };
  },
  async mounted() {
    this.matchQuery();
    this.initData();
    this.setChartLabels();
  },
  methods: {
    async initData() {
      this.loading = true;
      const result = await this.$http.get('/api/mkc/v1/sales-stats', {
        params: {
          ssd: moment(this.dateRange[0]).format('YYYY-MM-DD'),
          sed: moment(this.dateRange[1]).format('YYYY-MM-DD'),
          group: 'd',
        },
      });
      this.advertResults = result.data.list;
      this.advertResults.forEach((column) => {
        column.moneyAmountSum = column.moneyAmount + column.pointAmount;
      });
      const chartistDate = _.clone(this.advertResults);
      // const statsDate = result.data.list;
      chartistDate.sort((a, b) => {
        let resultSort = 0;
        if (a.statsDay < b.statsDay) {
          resultSort = -1;
        } else if (a.statsDay > b.statsDay) {
          resultSort = 1;
        } else {
          resultSort = 0;
        }
        return resultSort;
      });
      const buyCount = [];
      const validCount = [];
      const joinCount = [];
      chartistDate.forEach((data) => {
        buyCount.push(data.buyCount);
        validCount.push(data.validCount);
        joinCount.push(data.joinCount);
      });
      this.chartistDate = [buyCount, validCount, joinCount];
      this.loading = false;
    },
    setChartLabels() {
      const startDate = moment(this.dateRange[0]);
      const searchDates = [];
      _.times(moment(this.dateRange[1]).diff(moment(this.dateRange[0]), 'days'), () => {
        searchDates.push(startDate.format('MM-DD'));
        startDate.add(1, 'day');
      });
      this.searchDates = searchDates;
    },
    changeTab(tabName) {
      this.currentTab = tabName;
    },
    getSummaries({ columns, data }) {
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = '합계';
          return;
        }
        const values = data.map(item => Number(item[column.property]));
        if (!values.every(value => _.isNaN(value))) {
          sums[index] = values.reduce((prev, curr) => {
            const value = Number(curr);
            if (!_.isNaN(value)) return prev + curr;
            return prev;
          }, 0);
        } else {
          sums[index] = 'N/A';
        }

        if (column.property === 'buyAmount' || column.property === 'moneyAmountSum') {
          sums[index] = this.$options.filters.displayPrice(sums[index]);
        } else if (column.property === 'cvr') {
          if (sums[1] !== 'N/A' && sums[2] !== 'N/A') {
            sums[index] = this.$options.filters.displayRatio(Number(sums[2]), Number(sums[1]));
          }
        } else if (column.property === 'buyAverage') {
          if (sums[2] !== 'N/A' && sums[3] !== 'N/A') {
            sums[index] = this.$options.filters.displayPrice(this.$options.filters.displayAverage(Number(sums[3].replace(/[^0-9]/g, '')), Number(sums[2])));
          }
        } else if (column.property === 'cpcAverage') {
          if (sums[1] !== 'N/A' && sums[6] !== 'N/A') {
            sums[index] = this.$options.filters.displayPrice(this.$options.filters.displayAverage(Number(sums[6].replace(/[^0-9]/g, '')), Number(sums[1])));
          }
        } else if (column.property === 'roas') {
          if (sums[3] !== 'N/A' && sums[6] !== 'N/A') {
            sums[index] = this.$options.filters.displayRoas(Number(sums[3].replace(/[^0-9]/g, '')), Number(sums[6].replace(/[^0-9]/g, '')));
          }
        }
      });
      return sums;
    },
    matchQuery() {
      const query = { ...this.$route.query };
      if (query.startday !== undefined && query.endday !== undefined) {
        this.dateRange = [moment(query.startday, 'YY-MM-DD'), moment(query.endday, 'YY-MM-DD')];
      }
    },
    changeQuery(value, type) {
      const { path } = this.$route;
      const query = { ...this.$route.query };
      if (type === 'device') {
        query.device = value;
        this.device = value;
      } else if (type === 'date') {
        query.startday = moment(value[0]).format('YY-MM-DD');
        query.endday = moment(value[1]).format('YY-MM-DD');
        this.dateRange = [value[0], value[1]];
        this.setChartLabels();
      } else {
        query[type] = value;
      }
      this.$router.push({ path, query });
    },

    // 엑셀 다운로드
    async excelDownload() {
      const startDate = moment(this.dateRange[0]).format('YYYY-MM-DD');
      const endDate = moment(this.dateRange[1]).format('YYYY-MM-DD');
      const list = await this.$http.get('/api/mkc/v1/sales-stats', {
        params: {
          ssd: startDate,
          sed: endDate,
          group: 'd',
        },
      });

      const excelList = [];
      list.data.list.forEach((v) => {
        excelList.push([
          this.$options.filters.displayDate(v.statsDay),
          v.validCount,
          v.buyCount,
          v.buyAmount,
          this.$options.filters.displayRatio(v.buyCount, v.validCount),
          v.joinCount,
          v.moneyAmount,
          this.$options.filters.displayAverage(v.buyAmount, v.buyCount),
          this.$options.filters.displayAverage(v.moneyAmount, v.validCount),
          this.$options.filters.displayRoas(v.buyAmount, v.moneyAmount),
        ]);
      });

      const header = ['\uFEFF날짜', '클릭수', '주문건수', '매출', '구매전환율', '회원가입수', '광고비', '객단가', '평균 CPC', '성과율'].join(',');
      const csv = ['data:text/csv;charset=utf-8', [header, ...excelList].join('\r\n')].join(',');
      this.downloadUrl = encodeURI(csv);
      this.downloadName = `${startDate}_${endDate}_비즈머니내역.csv`;

      this.download = true;
    },

    clearDownload() {
      this.downloadName = null;
      this.downloadUrl = null;
      this.download = false;
    },
  },
};
</script>
